import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cancelOrder, fetchOrders, Order, OrderStatus, updateOrderStatus } from '../api/orders';
import {
  createDeliverer,
  DELIVERY_SCOPE_LABELS,
  DeliveryScope,
  fetchDelivererByUserId,
  updateDeliverer,
  updateDelivererAvailability
} from '../api/deliverers';
import { ApiError } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { PageHeader } from '../components/ui/PageHeader';
import { StatusBadge } from '../components/StatusBadge';
import { Spinner } from '../components/ui/Spinner';
import { useDelivererSocket } from '../hooks/useDelivererSocket';

const NEXT_STATUS: Array<{ current: OrderStatus; next: OrderStatus; label: string }> = [
  { current: 'pending', next: 'accepted', label: 'Je prends cette course' },
  { current: 'accepted', next: 'picked_up', label: 'J’ai le colis' },
  { current: 'picked_up', next: 'in_transit', label: 'Je suis en route' },
  { current: 'in_transit', next: 'delivered', label: 'J’ai remis le colis' }
];

export const DelivererDashboardPage = () => {
  const qc = useQueryClient();
  const { socket, connected, error } = useDelivererSocket();
  const [lat, setLat] = useState<number>(0);
  const [lng, setLng] = useState<number>(0);
  const [trackingCode, setTrackingCode] = useState<string>('');

  const ordersQuery = useQuery({
    queryKey: ['orders', { role: 'deliverer' }],
    queryFn: () => fetchOrders({ limit: 50, offset: 0 })
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: OrderStatus }) => updateOrderStatus(id, status),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orders'] });
    }
  });

  const cancelMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => cancelOrder(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['orders'] });
    }
  });

  const orders = useMemo<Order[]>(() => ordersQuery.data?.data ?? [], [ordersQuery.data]);

  const emitLocation = () => {
    if (!socket) return;
    if (!trackingCode.trim()) return;
    socket.emit('delivererLocation', {
      trackingCode: trackingCode.trim(),
      latitude: lat,
      longitude: lng,
      at: new Date().toISOString()
    });
  };

  const useBrowserGeo = () => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLat(pos.coords.latitude);
        setLng(pos.coords.longitude);
      },
      () => {
        // ignore
      },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Ma tournée"
        description="Dites si vous êtes disponible, prenez une course, puis avancez-la jusqu’à la porte. Le passé est dans Historique."
      />

      <DelivererProfilePanel />

      <div className="card p-5 space-y-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm">
            <span className="font-medium text-slate-900">Position :</span>{' '}
            {connected ? (
              <span className="text-emerald-700">connecté</span>
            ) : (
              <span className="text-slate-600">déconnecté</span>
            )}
            {error && <span className="ml-2 text-red-600">({error})</span>}
          </div>
          <button type="button" className="btn-ghost" onClick={useBrowserGeo}>
            Utiliser ma position
          </button>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <label className="space-y-1 md:col-span-2">
            <span className="text-sm font-medium text-slate-700">Code de la course</span>
            <input className="input" value={trackingCode} onChange={(e) => setTrackingCode(e.target.value)} />
          </label>
          <label className="space-y-1">
            <span className="text-sm font-medium text-slate-700">Latitude</span>
            <input
              className="input"
              type="number"
              step="any"
              value={lat}
              onChange={(e) => setLat(Number(e.target.value))}
            />
          </label>
          <label className="space-y-1">
            <span className="text-sm font-medium text-slate-700">Longitude</span>
            <input
              className="input"
              type="number"
              step="any"
              value={lng}
              onChange={(e) => setLng(Number(e.target.value))}
            />
          </label>
        </div>

        <div className="flex items-center justify-between">
          <p className="text-xs text-slate-500">
            La personne qui suit la course voit où vous êtes.
          </p>
          <button type="button" className="btn-primary" onClick={emitLocation} disabled={!socket || !trackingCode.trim()}>
            Envoyer ma position
          </button>
        </div>
      </div>

      <div className="card p-0 overflow-hidden">
        <div className="border-b border-slate-200/80 px-5 py-4">
          <h2 className="text-base font-semibold text-slate-900">Courses à faire</h2>
          <p className="text-sm text-slate-600">Appuyez sur le bouton pour passer à l’étape suivante.</p>
        </div>
        <div className="divide-y divide-slate-200/80">
          {ordersQuery.isLoading && <div className="p-5 text-sm text-slate-500">Chargement…</div>}
          {ordersQuery.isError && (
            <div className="p-5 text-sm text-red-600">Erreur: {(ordersQuery.error as Error).message}</div>
          )}
          {!ordersQuery.isLoading && orders.length === 0 && (
            <div className="p-5 text-sm text-slate-500">Aucune commande assignée.</div>
          )}

          {orders.map((o) => {
            const next = NEXT_STATUS.find((x) => x.current === o.status);
            return (
              <div key={o.id} className="p-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-slate-500">{o.trackingCode}</span>
                    <StatusBadge status={o.status} />
                  </div>
                  <div className="mt-1 text-sm text-slate-900 truncate">{o.pickupAddress} → {o.deliveryAddress}</div>
                  <div className="mt-1 text-xs text-slate-500">Client: {o.senderName} · Dest.: {o.recipientName}</div>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  {next && (
                    <button
                      type="button"
                      className="btn-primary"
                      disabled={updateMutation.isPending || cancelMutation.isPending}
                      onClick={() => updateMutation.mutate({ id: o.id, status: next.next })}
                    >
                      {next.label}
                    </button>
                  )}
                  {(o.status === 'pending' || o.status === 'accepted') && (
                    <button
                      type="button"
                      className="btn-ghost border border-red-200 text-red-700 hover:bg-red-50"
                      disabled={cancelMutation.isPending || updateMutation.isPending}
                      onClick={() =>
                        cancelMutation.mutate({
                          id: o.id,
                          reason: o.status === 'pending' ? 'Refus livreur' : 'Annulation après acceptation'
                        })
                      }
                    >
                      {o.status === 'pending' ? 'Refuser' : 'Annuler'}
                    </button>
                  )}
                  {!next && o.status !== 'pending' && o.status !== 'accepted' && (
                    <span className="text-xs text-slate-500">Aucune action</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// ============================================================
// PANNEAU PROFIL LIVREUR — rayon d'action, ville, tarification
// ============================================================

const VEHICLE_OPTIONS = [
  { value: 'bike', label: 'Vélo' },
  { value: 'motorbike', label: 'Moto' },
  { value: 'car', label: 'Voiture' },
  { value: 'van', label: 'Camionnette' },
  { value: 'small_truck', label: 'Petit camion' },
  { value: 'medium_truck', label: 'Camion moyen' },
  { value: 'large_truck', label: 'Grand camion' },
  { value: 'refrigerated', label: 'Réfrigéré' }
];

const SCOPES: DeliveryScope[] = ['city', 'district', 'region', 'country', 'international'];

const DelivererProfilePanel = () => {
  const { user } = useAuth();
  const qc = useQueryClient();
  const profileQuery = useQuery({
    queryKey: ['my-deliverer', user?.id],
    queryFn: () => fetchDelivererByUserId(user!.id),
    enabled: !!user,
    retry: false
  });

  // Si le user a le rôle "deliverer" mais n'a pas de profil Deliverer (création requise)
  const needsOnboarding = !!user && user.role === 'deliverer' && profileQuery.error instanceof ApiError && profileQuery.error.status === 404;

  if (!user) return null;

  return (
    <section className="card p-5 sm:p-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-900">Comment vous livrez</h2>
          <p className="mt-1 text-xs text-slate-500">
            Votre véhicule, votre zone et si vous êtes disponible. Disponible : les courses de la zone vous sont proposées.
          </p>
        </div>
        {profileQuery.data && (
          <AvailabilityToggle
            delivererId={profileQuery.data.data.id}
            isAvailable={profileQuery.data.data.isAvailable}
            onUpdated={() => qc.invalidateQueries({ queryKey: ['my-deliverer', user.id] })}
          />
        )}
      </div>

      {profileQuery.isLoading && (
        <div className="mt-4 flex items-center gap-2 text-sm text-slate-500">
          <Spinner className="h-4 w-4 border-2 border-slate-300 border-t-brand-500" />
          Chargement du profil…
        </div>
      )}

      {needsOnboarding && <OnboardDelivererForm userId={user.id} onCreated={() => profileQuery.refetch()} />}

      {profileQuery.error && !needsOnboarding && (
        <p className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {profileQuery.error instanceof ApiError
            ? profileQuery.error.message
            : 'Erreur de chargement du profil'}
        </p>
      )}

      {profileQuery.data && (
        <DelivererProfileForm
          deliverer={profileQuery.data.data}
          onUpdated={() => qc.invalidateQueries({ queryKey: ['my-deliverer', user.id] })}
        />
      )}
    </section>
  );
};

const AvailabilityToggle = ({
  delivererId,
  isAvailable,
  onUpdated
}: {
  delivererId: string;
  isAvailable: boolean;
  onUpdated: () => void;
}) => {
  const mutation = useMutation({
    mutationFn: (next: boolean) => updateDelivererAvailability(delivererId, next),
    onSuccess: onUpdated
  });
  return (
    <button
      type="button"
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium transition-all ${
        isAvailable
          ? 'bg-emerald-100 text-emerald-700 ring-1 ring-emerald-200 hover:bg-emerald-200'
          : 'bg-slate-100 text-slate-600 ring-1 ring-slate-200 hover:bg-slate-200'
      }`}
      onClick={() => mutation.mutate(!isAvailable)}
      disabled={mutation.isPending}
    >
      <span className={`h-2 w-2 rounded-full ${isAvailable ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'}`} />
      {mutation.isPending ? 'Mise à jour…' : isAvailable ? 'Disponible' : 'Indisponible'}
    </button>
  );
};

const OnboardDelivererForm = ({ userId, onCreated }: { userId: string; onCreated: () => void }) => {
  const [form, setForm] = useState({
    vehicleType: 'motorbike',
    deliveryScope: 'city' as DeliveryScope,
    baseCity: '',
    baseCountry: '',
    pricePerKm: 1.5,
    currency: 'EUR'
  });
  const mutation = useMutation({
    mutationFn: () => createDeliverer({ userId, ...form }),
    onSuccess: onCreated
  });

  return (
    <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-4">
      <p className="text-sm font-medium text-amber-900">
        Bienvenue ! Complétez votre profil livreur pour recevoir vos premières courses.
      </p>
      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label className="block">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-600">Véhicule</span>
          <select
            className="input mt-1.5"
            value={form.vehicleType}
            onChange={(e) => setForm((f) => ({ ...f, vehicleType: e.target.value }))}
          >
            {VEHICLE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-600">Rayon d'action</span>
          <select
            className="input mt-1.5"
            value={form.deliveryScope}
            onChange={(e) => setForm((f) => ({ ...f, deliveryScope: e.target.value as DeliveryScope }))}
          >
            {SCOPES.map((s) => (
              <option key={s} value={s}>{DELIVERY_SCOPE_LABELS[s]}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-600">Ville de base</span>
          <input
            className="input mt-1.5"
            placeholder="Libreville, Lagos, Casablanca, Paris…"
            value={form.baseCity}
            onChange={(e) => setForm((f) => ({ ...f, baseCity: e.target.value }))}
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-600">Pays (code ISO)</span>
          <input
            className="input mt-1.5"
            placeholder="GA, FR, NG…"
            maxLength={3}
            value={form.baseCountry}
            onChange={(e) => setForm((f) => ({ ...f, baseCountry: e.target.value.toUpperCase() }))}
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-600">Prix au km</span>
          <input
            type="number"
            min="0"
            step="0.1"
            className="input mt-1.5"
            value={form.pricePerKm}
            onChange={(e) => setForm((f) => ({ ...f, pricePerKm: Number(e.target.value) }))}
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-600">Devise</span>
          <input
            className="input mt-1.5"
            value={form.currency}
            maxLength={3}
            onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value.toUpperCase() }))}
          />
        </label>
      </div>
      {mutation.error && (
        <p className="mt-3 text-sm text-red-700">
          {mutation.error instanceof ApiError ? mutation.error.message : 'Erreur de création'}
        </p>
      )}
      <div className="mt-4">
        <button type="button" className="btn-primary" disabled={mutation.isPending} onClick={() => mutation.mutate()}>
          {mutation.isPending ? 'Création…' : 'Créer mon profil livreur'}
        </button>
      </div>
    </div>
  );
};

const DelivererProfileForm = ({
  deliverer,
  onUpdated
}: {
  deliverer: import('../api/deliverers').DelivererRow;
  onUpdated: () => void;
}) => {
  const [form, setForm] = useState({
    vehicleType: deliverer.vehicleType,
    deliveryScope: (deliverer.deliveryScope ?? 'city') as DeliveryScope,
    baseCity: deliverer.baseCity ?? '',
    baseCountry: deliverer.baseCountry ?? '',
    maxDistance: deliverer.maxDistance ?? 0,
    pricePerKm: deliverer.pricePerKm ?? 0,
    minDeliveryPrice: deliverer.minDeliveryPrice ?? 0,
    currency: deliverer.currency || 'EUR',
    companyName: deliverer.companyName ?? ''
  });

  useEffect(() => {
    setForm({
      vehicleType: deliverer.vehicleType,
      deliveryScope: (deliverer.deliveryScope ?? 'city') as DeliveryScope,
      baseCity: deliverer.baseCity ?? '',
      baseCountry: deliverer.baseCountry ?? '',
      maxDistance: deliverer.maxDistance ?? 0,
      pricePerKm: deliverer.pricePerKm ?? 0,
      minDeliveryPrice: deliverer.minDeliveryPrice ?? 0,
      currency: deliverer.currency || 'EUR',
      companyName: deliverer.companyName ?? ''
    });
  }, [deliverer.id]);

  const mutation = useMutation({
    mutationFn: () =>
      updateDeliverer(deliverer.id, {
        vehicleType: form.vehicleType,
        deliveryScope: form.deliveryScope,
        baseCity: form.baseCity || undefined,
        baseCountry: form.baseCountry || undefined,
        maxDistance: form.maxDistance || undefined,
        pricePerKm: form.pricePerKm || undefined,
        minDeliveryPrice: form.minDeliveryPrice || undefined,
        currency: form.currency,
        companyName: form.companyName || undefined
      }),
    onSuccess: onUpdated
  });

  return (
    <form
      className="mt-5 space-y-5"
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate();
      }}
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <label className="block">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-600">Véhicule</span>
          <select
            className="input mt-1.5"
            value={form.vehicleType}
            onChange={(e) => setForm((f) => ({ ...f, vehicleType: e.target.value }))}
          >
            {VEHICLE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-600">
            Rayon d'action de livraison
          </span>
          <select
            className="input mt-1.5"
            value={form.deliveryScope}
            onChange={(e) => setForm((f) => ({ ...f, deliveryScope: e.target.value as DeliveryScope }))}
          >
            {SCOPES.map((s) => (
              <option key={s} value={s}>{DELIVERY_SCOPE_LABELS[s]}</option>
            ))}
          </select>
          <p className="mt-1 text-[11px] text-slate-500">
            Local = uniquement votre ville · International = expéditions à travers le monde.
          </p>
        </label>
        <label className="block">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-600">Nom commercial</span>
          <input
            className="input mt-1.5"
            placeholder="Optionnel (entreprise)"
            value={form.companyName}
            onChange={(e) => setForm((f) => ({ ...f, companyName: e.target.value }))}
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-600">
            Distance maximale (km)
          </span>
          <input
            type="number"
            min="0"
            className="input mt-1.5"
            placeholder="0 = pas de limite"
            value={form.maxDistance}
            onChange={(e) => setForm((f) => ({ ...f, maxDistance: Number(e.target.value) }))}
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-600">Ville de base</span>
          <input
            className="input mt-1.5"
            placeholder="Libreville, Lagos, Casablanca, Paris…"
            value={form.baseCity}
            onChange={(e) => setForm((f) => ({ ...f, baseCity: e.target.value }))}
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-600">
            Pays (code ISO à 2-3 lettres)
          </span>
          <input
            className="input mt-1.5"
            placeholder="GA, FR, NG, MA…"
            maxLength={3}
            value={form.baseCountry}
            onChange={(e) => setForm((f) => ({ ...f, baseCountry: e.target.value.toUpperCase() }))}
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-600">Prix au km</span>
          <input
            type="number"
            min="0"
            step="0.1"
            className="input mt-1.5"
            value={form.pricePerKm}
            onChange={(e) => setForm((f) => ({ ...f, pricePerKm: Number(e.target.value) }))}
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-600">Prix minimum</span>
          <input
            type="number"
            min="0"
            step="0.5"
            className="input mt-1.5"
            value={form.minDeliveryPrice}
            onChange={(e) => setForm((f) => ({ ...f, minDeliveryPrice: Number(e.target.value) }))}
          />
        </label>
        <label className="block">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-600">Devise</span>
          <input
            className="input mt-1.5"
            value={form.currency}
            maxLength={3}
            onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value.toUpperCase() }))}
          />
        </label>
      </div>

      {mutation.error && (
        <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {mutation.error instanceof ApiError ? mutation.error.message : 'Erreur de mise à jour'}
        </p>
      )}
      {mutation.isSuccess && (
        <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
          ✓ Profil mis à jour avec succès
        </p>
      )}

      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-slate-500">
          📍 Rayon actuel : <strong>{DELIVERY_SCOPE_LABELS[form.deliveryScope]}</strong>
        </p>
        <button type="submit" className="btn-primary" disabled={mutation.isPending}>
          {mutation.isPending ? 'Enregistrement…' : 'Enregistrer'}
        </button>
      </div>
    </form>
  );
};

